// Easter Egg: "Chuck Norris" preset in the DifficultyLegend filter sheet
// opens the legendary "Everest" trail directly on the map — full route from
// Lukla up to a creatively-rounded Mt. Everest summit, draped over satellite
// imagery for maximum drama.
//
// Wired via a `window` CustomEvent (no prop-drilling through BottomBar).
// DifficultyLegend dispatches `EVEREST_EASTER_EGG_EVENT` on click; MapPage
// listens, swaps `selected` to this synthetic trail (the existing `selected`
// useEffect then takes care of `fitBoundsToTrail`), and forces the basemap
// style to 'satellite'.
//
// The Everest trail is purely synthetic — there is no row in the trails
// table. The `id` uses the prefix `easter:` so it won't collide with real
// trail IDs (slugs/UUIDs without colon), with `run:` (DH runs) or `custom:`
// (user-drawn routes). MapPage's `mapTrails` builder pushes this synthetic
// trail's track into the rendered list when selected, the same way it does
// for `custom:` pseudo-trails.

import type { CachedTrail } from './trails';

// Wave 14dq: ID-Bump auf '-v2' — invalidiert jeden alten IDB-Cache-Eintrag
// (alte SRTM-Samples bei 8685m). useTrailElevation lookt auf den string
// und cached pro ID, der alte Eintrag bleibt unangetastet auf Disk, wird
// aber nie wieder gelesen. Next tap auf Chuck Norris baut den Trail
// mit der neuen ID, läuft durch den Bypass, produziert 8850.
export const EVEREST_EASTER_EGG_ID = 'easter:everest-v2';
export const EVEREST_EASTER_EGG_EVENT = 'adt:easter-egg-everest';

export function isEverestEasterEggId(id: string | null | undefined): boolean {
  return id === EVEREST_EASTER_EGG_ID;
}

/** 18 waypoints along the standard Lukla → South Col → Summit route. The
 *  elevations are picked so that, summed over the polyline, total ascent
 *  ≈ 5455 m and total descent ≈ 203 m (matching the values shown in the
 *  Easter-egg mockup), with min ele = 2779 m and max ele = 8030 m. The
 *  GPS coordinates are roughly the real-world locations of each waypoint
 *  along the South Col route — close enough that on satellite imagery the
 *  polyline visibly snakes up the actual Khumbu valley. */
const EVEREST_WAYPOINTS: { lat: number; lon: number; ele: number }[] = [
  { lat: 27.6870, lon: 86.7311, ele: 2779 }, // Lukla — start (min ele)
  { lat: 27.7140, lon: 86.7220, ele: 2820 }, // approach to Phakding
  { lat: 27.8060, lon: 86.7130, ele: 3440 }, // Namche Bazaar
  { lat: 27.8200, lon: 86.7400, ele: 3650 }, // climb to Tengboche ridge
  { lat: 27.8360, lon: 86.7640, ele: 3867 }, // Tengboche
  { lat: 27.8400, lon: 86.7720, ele: 3815 }, // dip after Tengboche  (-52 m)
  { lat: 27.8520, lon: 86.7890, ele: 3985 }, // Pangboche
  { lat: 27.8920, lon: 86.8310, ele: 4410 }, // Dingboche
  { lat: 27.9480, lon: 86.8080, ele: 4910 }, // Lobuche
  { lat: 27.9970, lon: 86.8280, ele: 5160 }, // Gorak Shep
  { lat: 28.0030, lon: 86.8530, ele: 5364 }, // Everest Base Camp
  { lat: 27.9990, lon: 86.8590, ele: 5310 }, // Khumbu Icefall dip   (-54 m)
  { lat: 27.9980, lon: 86.8700, ele: 5943 }, // Camp 1 (Western Cwm)
  { lat: 27.9870, lon: 86.8810, ele: 5895 }, // dip in the Cwm        (-48 m)
  { lat: 27.9850, lon: 86.8920, ele: 6500 }, // Camp 2 (Advanced BC)
  { lat: 27.9690, lon: 86.9100, ele: 7150 }, // Camp 3 (Lhotse Face)
  { lat: 27.9650, lon: 86.9200, ele: 7101 }, // dip near Yellow Band  (-49 m)
  { lat: 27.9620, lon: 86.9280, ele: 7906 }, // South Col (Camp 4)
  // Wave 14dj: Gipfel exakt auf 8850 m gesetzt — User-Wunsch: "da MUSS
  // stehen: 8850 weil es die höhe von everest ist". (Offiziell 8848.86 m,
  // 2020 Joint-Survey; 8850 ist die saubere Roundzahl die historisch
  // verwendet wird.) Vorher 8030 — SRTM-HTTP-Sampler überschrieb den
  // Wert ohnehin mit ~8685 (SRTM hat eine bekannte Gipfel-Unterschätzung),
  // daher zusätzlich Pipeline-Bypass im useTrailElevation für diesen Trail.
  { lat: 27.9750, lon: 86.9270, ele: 8850 }, // summit (Mt. Everest)
];

/** Build the synthetic Everest CachedTrail. New track instance per call so
 *  React reference-equality checks treat each easter-egg activation as a
 *  fresh selection (forces fitBounds + ElevationProfile recompute). */
export function buildEverestEasterEggTrail(): CachedTrail {
  return {
    id: EVEREST_EASTER_EGG_ID,
    name: 'Everest',
    // Difficulty 'extreme' gives the black-and-white striped swatch in the
    // header dot and the "· Extreme" suffix in the trail-type subtitle.
    difficulty: 'extreme',
    // trailType is rendered raw; capitalize-CSS in MapPage turns this into
    // "Downhill · Extreme" in the detail card.
    trailType: 'Downhill',
    colorHex: null,
    description:
      "Chuck Norris doesn't navigate trails. Trails navigate around Chuck Norris.",
    photos: [],
    gpxFilename: 'easter-everest.gpx',
    track: {
      name: 'Everest',
      points: EVEREST_WAYPOINTS.map((p) => ({
        lat: p.lat,
        lon: p.lon,
        ele: p.ele,
      })),
    },
    cruxPoints: [],
    // Length is hard-coded to match the screenshot — the rough sum of
    // great-circle distances between waypoints is in the same ballpark
    // (~60-65 km depending on smoothing), so an exact 65.27 km label is
    // inside-the-noise.
    stats: {
      lengthMeters: 65270,
      gain: 5455,
      loss: 203,
    },
    cachedAt: new Date().toISOString(),
  };
}
